import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {firstValueFrom, iif, Observable, of, Subject, switchMap, takeUntil, tap,} from 'rxjs';
import {UserCompanyList, UserRequest, UserResponse,} from '../../repository/entity/user.entity';
import {UserService} from '../../service/user.service';
import {NgxSpinnerService} from 'ngx-spinner';
import {ToastrService} from 'ngx-toastr';
import {ActivatedRoute, Router} from '@angular/router';

@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss'],
})
export class UserFormComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  isLoading: boolean = false;
  onDestroy$ = new Subject<void>();
  data: UserResponse | undefined;
  idParam: string;

  userCompanyOptions$: Observable<UserCompanyList[]>;

  constructor(
    private userService: UserService,
    private spinner: NgxSpinnerService,
    private toastService: ToastrService,
    private route: ActivatedRoute,
    private router: Router
  ) {
  }

  private get customPayload(): UserRequest {
    const userCompany = this.form
      .get('userCompany')
      ?.value.map((item: string) => {
        return {
          companyCode: item,
        };
      });

    const payload: UserRequest = {
      ...this.form.value,
      userCompany,
    };

    return payload;
  }

  async ngOnInit(): Promise<void> {
    this.idParam = this.route.snapshot.params['id'];
    this.form = new FormGroup({
      username: new FormControl(null, [Validators.required]),
      password: new FormControl(null, [Validators.required]),
      fullName: new FormControl(null, [Validators.required]),
      email: new FormControl(null),
      userCompany: new FormControl([], [Validators.required]),
    });

    this.userCompanyOptions$ = this.userService.listUserCompany$;

    const getUserCompany$ = this.getUserCompany();
    const initDataAction$ = getUserCompany$.pipe(
      takeUntil(this.onDestroy$),
      switchMap(() => {
        const condition = !!this.idParam;
        return iif(() => condition, this.getUser(this.idParam), of(null));
      })
    );

    await firstValueFrom(initDataAction$);
  }

  getUserCompany() {
    return this.userService.fetchListUserCompany();
  }

  getUser(username: string) {
    return this.userService.fetchUserByUsername(username).pipe(
      takeUntil(this.onDestroy$),
      tap((value) => {
        this.data = value?.result;
        if (this.data) {
          this.form.patchValue({
            username: this.data.username,
            fullName: this.data.fullName,
            email: this.data.email,
            userCompany: this.data.userCompany.map((item) => item.companyCode),
          });
        }
      })
    );
  }

  onCancelClick(): void {
    this.form.reset();
    this.router.navigate(['user']);
  }

  async onSubmitClick(): Promise<void> {
    this.form.updateValueAndValidity();
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }

    if (this.idParam) {
      this._onUpdate();
    } else {
      this._onCreate();
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private async _onCreate() {
    await this.spinner.show();
    const createAction$ = this.userService
      .createUser(this.customPayload)
      .pipe(takeUntil(this.onDestroy$));
    const response = await firstValueFrom(createAction$);
    if (response?.result) {
      this.toastService.success(
        `User ${response.result.fullName} has been created!`
      );
      await this.router.navigate(['user']);
      await this.spinner.hide();
    }
  }

  private async _onUpdate() {
    await this.spinner.show();
    const updateAction$ = this.userService
      .updateUser(this.customPayload)
      .pipe(takeUntil(this.onDestroy$));
    const response = await firstValueFrom(updateAction$);
    if (response?.result) {
      this.toastService.success(
        `User ${response.result.fullName} has been updated!`
      );
      await this.router.navigate(['user']);
      await this.spinner.hide();
    }
  }
}
